import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TenantUserListItemSchema } from '@/entities/tenantusers/api/generated/model';
import { Button, Form, Grid, InputField, LabeledText, Text } from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { type ResetPasswordFormValues, resetPasswordSchema } from './schema';
import { useResetPasswordMutation } from './useResetPasswordMutation';

type Props = {
  user: TenantUserListItemSchema;
  onClose: () => void;
};

export const ResetPasswordForm = ({ user, onClose }: Props) => {
  const { t } = useTranslation();

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '' },
  });

  const resetMutation = useResetPasswordMutation({ onSuccess: onClose });
  const isPending = resetMutation.isPending;

  const handleSubmit = (values: ResetPasswordFormValues) => {
    resetMutation.mutate({ userId: user.id, data: values });
  };

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <Text variant="body" className="mb-4 block">
            {t('user-management.users.reset-password.description', {
              name: user.name,
              email: user.email,
            })}
          </Text>
          <Grid
            columns={1}
            items={[
              [
                {
                  key: 'name',
                  field: (
                    <LabeledText label={t('user-management.field.name')}>{user.name}</LabeledText>
                  ),
                },
                {
                  key: 'email',
                  field: (
                    <LabeledText label={t('user-management.field.email')}>{user.email}</LabeledText>
                  ),
                },
                {
                  key: 'password',
                  field: (
                    <InputField
                      control={form.control}
                      name="password"
                      type="password"
                      label={t('common.fields.new-password')}
                      required
                      autoComplete="new-password"
                      supportText={t('user-management.users.password-rule')}
                      showSupportText
                    />
                  ),
                },
              ],
            ]}
          />
        </div>
        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('user-management.users.reset-password.submit')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
